import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Search, CalendarCheck } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { api } from '../../lib/api';

const STATUS_STYLE = {
  green: { dot: 'bg-[#15803d]', chip: 'bg-[#dcfce7] text-[#166534]', label: 'Completa' },
  orange: { dot: 'bg-[#d97706]', chip: 'bg-[#fef3c7] text-[#92400e]', label: 'Parcial' },
  red: { dot: 'bg-[#b91c1c]', chip: 'bg-[#fee2e2] text-[#991b1b]', label: 'Sin tracking' },
};
const FILTERS = [
  { key: 'all', label: 'Todas' },
  { key: 'green', label: 'Completas' },
  { key: 'orange', label: 'Parciales' },
  { key: 'red', label: 'Sin tracking' },
];

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const fmtT = (iso) => new Date(iso).toLocaleTimeString('es-GT', { hour: 'numeric', minute: '2-digit' });
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// AD-P19 · Cumplimiento de trackings diarios por estación (con TM).
export default function AdminTrackingCompliance() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusF, setStatusF] = useState('all');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/tracking-compliance', { params: { date } });
      setData(r.data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const target = data?.target || 3;
  const stations = (data?.stations || []).filter(s => {
    if (statusF !== 'all' && s.status !== statusF) return false;
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return [s.name, s.bu, s.region, s.tm].some(x => (x || '').toLowerCase().includes(t));
  });

  return (
    <div className="space-y-6" data-testid="admin-tracking-compliance-page">
      <div>
        <h2 className="font-serif text-[36px] leading-tight tracking-tight">Cumplimiento tracking</h2>
        <p className="text-[13px] text-[#5a5a5a] mt-1">
          Trackings diarios completados por estación (meta = slots del horario de cada estación para el día) · día corta a medianoche GT · cuentan solo rondas completadas.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="date"
          value={date}
          max={todayISO()}
          onChange={e => e.target.value && setDate(e.target.value)}
          className="w-[160px] h-9 bg-white text-[12.5px]"
          data-testid="compliance-date"
        />
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar estación, TM, zona, BU…" className="h-9 pl-8 bg-white text-[12.5px] rounded-full" data-testid="compliance-search"/>
        </div>
        <div className="inline-flex gap-1 bg-white border border-[#e8e5de] rounded-full p-1">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setStatusF(f.key)}
              className={`px-3.5 py-1.5 rounded-full text-[12px] transition ${statusF === f.key ? 'bg-[#1c1c1c] text-[#f8f6f1]' : 'text-[#5a5a5a] hover:bg-[#f3f0e9]'}`}
              data-testid={`compliance-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {data && (
          <span className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full bg-[#e7eeff] text-[#0f1d6f]" data-testid="compliance-summary">
            <CalendarCheck size={13}/> {data.complete} de {data.total} completas
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
      ) : stations.length === 0 ? (
        <div className="bg-white border border-[#e8e5de] rounded-xl p-10 text-center">
          <p className="text-[13px] text-[#8a8579]">Sin estaciones que coincidan.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#f8f6f1]">
                <tr className="text-left text-[10.5px] tracking-wider uppercase text-[#8a8579] border-b border-[#e8e5de]">
                  <th className="py-2.5 px-4 font-normal">Estación</th>
                  <th className="py-2.5 px-4 font-normal">TM</th>
                  <th className="py-2.5 px-4 font-normal">Zona</th>
                  <th className="py-2.5 px-4 font-normal text-center">Hoy</th>
                  <th className="py-2.5 px-4 font-normal">Último tracking</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e8e5de]">
                {stations.map(s => {
                  const st = STATUS_STYLE[s.status] || STATUS_STYLE.red;
                  const open = openId === s.id;
                  return (
                    <React.Fragment key={s.id}>
                      <tr
                        className="hover:bg-[#fafaf6] cursor-pointer"
                        onClick={() => setOpenId(open ? null : s.id)}
                        title="Clic para ver horas de los trackings del día"
                        data-testid={`compliance-row-${s.id}`}
                      >
                        <td className="py-2.5 px-4">
                          <div className="flex items-center gap-2.5">
                            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${st.dot}`} data-testid={`semaforo-${s.id}`}/>
                            <div>
                              <p className="font-medium">{s.name}</p>
                              <p className="text-[10.5px] text-[#8a8579]">{s.brand}{s.bu ? ` · BU ${s.bu}` : ''}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 px-4 text-[#3a3a3a]">{s.tm || <span className="text-[#a8a397]">—</span>}</td>
                        <td className="py-2.5 px-4 text-[#5a5a5a]">{s.region || '—'}</td>
                        <td className="py-2.5 px-4 text-center">
                          <span className={`text-[11.5px] font-mono px-2 py-0.5 rounded-full ${st.chip}`} data-testid={`compliance-count-${s.id}`}>
                            {s.rounds_today}/{s.target ?? target}
                          </span>
                          {s.incomplete_today > 0 && (
                            <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e]" title="Trackings incompletos hoy (no cuentan para la meta)" data-testid={`compliance-incomplete-${s.id}`}>
                              +{s.incomplete_today} inc.
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-4 font-mono tabular-nums text-[12px] text-[#3a3a3a]" data-testid={`compliance-last-${s.id}`}>
                          {fmtDT(s.last_tracking_at)}
                          {s.last_tracking_at && s.last_incomplete && (
                            <span className="ml-1.5 text-[10px] font-sans px-1.5 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e]">incompleto</span>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-[#fafaf6]" data-testid={`compliance-detail-${s.id}`}>
                          <td colSpan={5} className="py-2 px-4 pl-11">
                            {s.times_today.length > 0 ? (
                              <span className="text-[11.5px] text-[#3a3a3a]">
                                Trackings del día:{' '}
                                {s.times_today.map((t, i) => (
                                  <span key={i} className={`inline-block font-mono px-2 py-0.5 rounded-full mr-1.5 ${t.complete ? 'bg-[#f3f0e9]' : 'bg-[#fef3c7] text-[#92400e]'}`} title={t.complete ? 'Completado' : 'Incompleto (no cuenta)'}>
                                    {t.start ? `${fmtT(t.start)} – ${fmtT(t.at)}` : fmtT(t.at)}{!t.complete && ' · inc.'}
                                  </span>
                                ))}
                              </span>
                            ) : (
                              <span className="text-[11.5px] text-[#8a8579]">Sin trackings este día.</span>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-[#8a8579] px-4 py-2 border-t border-[#e8e5de] bg-[#f8f6f1]">
            🟢 meta cumplida · 🟡 en progreso · 🔴 sin tracking. Clic en una fila para ver las horas.
          </p>
        </div>
      )}
    </div>
  );
}
